import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';

import { useEventsForPatients } from '../api/events.api.js';
import useWebSocketMessage from '../hooks/websocket-hooks/useWebSocketMessage.js';
import { type Event, type WebSocketMessage, WebSocketMessageTypes } from '../models/index.js';
import useWebSocket from '../providers/useWebSocket.js';

export enum EventMessageCategory {
  EVENT_CREATED = 'EVENT_CREATED',
  EVENT_UPDATED = 'EVENT_UPDATED',
  MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
  MATCHING_UPDATED = 'MATCHING_UPDATED',
}

interface EventMessage {
  category: EventMessageCategory;
  event: Event;
}

type Events = {
  activeEvents: Event[];
  upcomingEvents: Event[];
};

type UseEventsReturn = Events & {
  isLoading: boolean;
  refetch: () => void;
};

const SORT_A_FIRST = -1;
const SORT_B_FIRST = 1;

const useEvents = (patientIds: string[], onMessage?: (message: WebSocketMessage<EventMessage>) => void): UseEventsReturn => {
  const lastMessage = useWebSocketMessage<EventMessage>(WebSocketMessageTypes.EVENT);
  const {
    data: eventsFromApi,
    isLoading,
    refetch,
  } = useEventsForPatients({ patientIds }, { enabled: !!patientIds.length });

  const [unsortedEvents, setUnsortedEvents] = useState<Event[]>([]);
  const [events, setEvents] = useState<Events>({
    activeEvents: [],
    upcomingEvents: [],
  });

  useEffect(() => {
    if (eventsFromApi) {
      setUnsortedEvents(eventsFromApi);
    }
  }, [JSON.stringify(eventsFromApi), isLoading]);

  useEffect(() => {
    if (lastMessage) {
      const { data: { category, event } } = lastMessage;
      const isSubscribed = event && patientIds.includes(event.patientId);

      if (isSubscribed) {
        switch (category) {
          case EventMessageCategory.EVENT_CREATED:
            if (event) {
              setUnsortedEvents((previousEvents) => previousEvents.concat(event));
            }

            break;

          case EventMessageCategory.EVENT_UPDATED:
            if (event) {
              if (event.end) {
                // Remove an event once it is completed
                setUnsortedEvents((previousEvents) =>
                  previousEvents.filter((existingEvent) => existingEvent.id !== event.id),
                );
              } else {
                // Replace the old event data with the new event data
                // https://stackoverflow.com/a/62213534
                setUnsortedEvents((previousEvents) =>
                  previousEvents.map((existingEvent) => (existingEvent.id !== event.id ? existingEvent : event)),
                );
              }
            }

            break;

          case EventMessageCategory.MATCHING_UPDATED:
            // Refetching events is best handled via the onMessage() callback
            break;

          case EventMessageCategory.MESSAGE_RECEIVED:
            break;

          default:
            // Maybe there was no event or maybe the category didn't match a case here
            break;
        }
        if (onMessage) {
          onMessage(lastMessage);
        }
      }
    }
  }, [lastMessage, patientIds, setEvents]);

  useEffect(() => {
    // Collect and sort all events
    const orderedEvents = unsortedEvents.sort((a, b) => {
      // If priorities match, use date to sort...
      if (a.priority === b.priority) {
        return a.start < b.start ? SORT_A_FIRST : SORT_B_FIRST;
      }

      // ... Else, use priority to sort
      return a.priority < b.priority ? SORT_A_FIRST : SORT_B_FIRST;
    });

    const now = DateTime.now().toUTC().toISO();
    const sortedEvents = orderedEvents.reduce<Events>(
      (accumulator, event) => {
        if (event.start <= now) {
          accumulator.activeEvents.push(event);
        } else {
          accumulator.upcomingEvents.push(event);
        }

        return accumulator;
      },
      {
        activeEvents: [],
        upcomingEvents: [],
      },
    );

    setEvents(sortedEvents);
  }, [unsortedEvents]);

  return {
    ...events,
    isLoading,
    refetch,
  };
};

export default useEvents;
